import React, { useState, useMemo, Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Collapse,
  IconButton,
  Table as MuiTable,
  TableProps as MuiTableProps,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Tooltip,
  Theme,
  Checkbox,
  Fade,
  Box,
  Grid,
  TableRowProps,
  GridProps,
} from '@material-ui/core';

import ArrowDownwardRoundedIcon from '@material-ui/icons/ArrowDownwardRounded';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { StringKeys, Filter, PassableCellProps, FilterStateMap, FilterType, ItemAction } from './types';
import FilterComponent from './Filters/Filter'; 
import { isMoment } from 'moment';
import { LoadingRing } from 'components/Loading/Loading';

const printCell = (content: unknown) => {
  if (isMoment(content)) {
    return content.format('Do MMM YYYY')
  }
  if (content instanceof Date) {
    return content.toDateString();
  }
  if (content == null) {
    return '';
  }

  return content.toString();
}

type SortState = 'asc' | 'desc' | 'none';
const sortStateMachine: Record<SortState, SortState> = {
  asc: 'none',
  desc: 'asc',
  none: 'desc'
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1),
    overflow: 'auto',
    boxShadow: theme.shadows[5],
    borderRadius: 4
  },
  content: {
    padding: 0,
    '&:last-child': {
      padding: 0,
    }
  },
  inner: {
    minWidth: 700
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center'
  },
  avatar: {
    height: 42,
    width: 42,
    marginRight: theme.spacing(1)
  },
  popper: {
    // zIndex: theme.zIndex.tooltip + 100
  },
  actions: {
    padding: theme.spacing(1),
    justifyContent: 'flex-end'
  },
  itemActions: {
    opacity: 0,
    transition: 'opacity 300ms',
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(1)
    }
  },
  row: {
    '&:hover $itemActions': {
      opacity: 1
    }
  },
  capitalize: {
    textTransform: 'capitalize',
    position: 'relative',
    paddingRight: theme.spacing(2) + 20
  },
  loading: {
    padding: theme.spacing(1.5)
  },
  overflow: {
    overflowX: 'auto'
  }
}));

export type SortStateObj<T,K extends StringKeys<T>[]> = { key?: K[number], state?: SortState };
export type FilterStateObj<T,K extends StringKeys<T>[]> = Partial<Record<K[number], FilterStateMap[FilterType]>>;

type TableProps<T, K extends StringKeys<T>[]> = {
  data: T[],
  headers: K,
  classes?: {
    root?: string,
    wrapper?: string
  },
  title: string,
  idKey: string, // no need to use K since id might not be in the headers

  open?: boolean,
  setOpen?: Dispatch<SetStateAction<boolean>>,

  sortable?: boolean | K
  selectable?: boolean
  filters?: Partial<Record<K[number], Filter>>
  actions?: React.ReactNode | ((selectedIds: string[], setSelectedIds: (newState: string[]) => void) => React.ReactNode)
  itemActions?: ItemAction<T>[]
  loading?: boolean
  total?: number

  selectedIds?: string[]
  setSelectedIds?: React.Dispatch<React.SetStateAction<string[]>>

  // These props below are for more complex customizations
  headerAlign?: Partial<Record<K[number], GridProps['justifyContent']>>
  cellMap?: Partial<Record<K[number], React.ReactElement | ((row: T) => React.ReactNode)>>
  cellProps?: Partial<Record<K[number], PassableCellProps>>
  headerMap?: Partial<Record<K[number], string | React.ReactElement>>
  headerProps?: Partial<Record<K[number], PassableCellProps>>
  tableRowProps?: (row: T) => TableRowProps
  additionalColumns?: {
    id: string
    header: React.ReactNode,
    headerProps?: PassableCellProps,
    cell: React.ReactElement | ((row: T) => React.ReactNode),
    cellProps?: PassableCellProps
  }[]
  selectableAll?: boolean,
  selectedAll?: boolean,
  onSelectedAll?: (isChecked: boolean) => void,
  onDataChange?: (value: {total: number, selectedIds: string[], selectedAll:boolean}) => void
}
type TableImplementationProps<T, K extends StringKeys<T>[]> = {
  // Required to implement
  filterStateMap: FilterStateObj<T,K>,
  setFilterStateMap: Dispatch<SetStateAction<FilterStateObj<T,K>>>
  sortState: SortStateObj<T,K>
  setSortState: Dispatch<SetStateAction<SortStateObj<T,K>>>
  page: number,
  setPage: Dispatch<SetStateAction<number>>
  rowsPerPage: number,
  setRowsPerPage: Dispatch<SetStateAction<number>>
};

export type BaseTableProps<T, K extends StringKeys<T>[]> = MuiTableProps & TableProps<T, K> 
type Props<T, K extends StringKeys<T>[]> = BaseTableProps<T,K> & TableImplementationProps<T, K>
const BaseTable = <T, K extends StringKeys<T>[]>(props: Props<T, K>) => {
  const {
    classes = {},
    title,
    data,
    headers,
    idKey,
    actions,
    itemActions,
    selectable,
    filters = {} as Partial<Record<K[number], Filter>>,
    open: openProp,
    setOpen: setOpenProp,
    sortable = true,
    cellMap,
    cellProps,
    headerProps,
    headerMap,
    headerAlign,
    selectedIds: selectedIdsProps,
    setSelectedIds: setSelectedIdsProps,
    tableRowProps,
    additionalColumns = [],
    loading,
    total = data.length,
    sortState, setSortState, filterStateMap, setFilterStateMap, page, setPage, setRowsPerPage, rowsPerPage,
    selectableAll,
    selectedAll,
    onSelectedAll,
    onDataChange,
    ...rest
  } = props;

  const canSort = useCallback((column: K[number]) => typeof sortable === 'boolean' ? sortable:sortable.includes(column),[sortable])

  const styles = useStyles();
  const [openState, setOpenState] = useState(true);
  const open = openProp !== undefined ? openProp : openState;
  const setOpen = openProp !== undefined ? setOpenProp : setOpenState
  const [selectedIdsState, setSelectedIdsState] = useState<string[]>([]);
  // const [selectedAll, setSelectedAll] = useState<boolean>(false);

  const [selectedIds, setSelectedIds] = (selectedIdsProps && setSelectedIdsProps) ? [selectedIdsProps, setSelectedIdsProps] : [selectedIdsState, setSelectedIdsState];
  const [headerHover, setHeaderHover] = useState<K[number]>(null);
  const [currentSelectedIds, setCurrentSelectedIds] = useState<string[]>([]);


  const handleSort = useCallback((header: K[number]) => (e) => {
    if (canSort(header)) {
      setSortState((pv) => {
        if (pv.key === header) {
          return { ...pv, state: sortStateMachine[pv.state] };
        }
        return { key: header, state: sortStateMachine.none };
      });
    }
    if (headerProps && headerProps[header] && headerProps[header].onClick) {
      headerProps[header].onClick(e);
    }
  }, [setSortState, headerProps, canSort])

  useEffect(() => {
    setCurrentSelectedIds(selectedIds.filter((value) => data.map((value) => value[idKey]).includes(value)));
  }, [data, selectedIds, idKey])

  const handleChangePage = useCallback((event, page) => {
    setPage(page);
  }, []);

  const handleExpand = useCallback(() => {
    setOpen(pv => !pv);
  }, []);

  const handleChangeRowsPerPage = useCallback(event => {
    setPage(0);
    setRowsPerPage(event.target.value);
  }, []);

  const handleSelectOne = useCallback((id) => {
    setSelectedIds((oldIds: string[]) => {
      // Attempt to remove the id
      const newSelection = oldIds.filter(oldId => oldId !== id);
      // If nothing removed then we need to add the id
      if (newSelection.length === oldIds.length) {
        newSelection.push(id);
      }

      return newSelection
    }); 
  }, [setSelectedIds]);

  const handleFilter = useCallback((header: K[number]) => (state) => {
    setFilterStateMap(pv => ({...pv, [header]: state}));
  }, []);

  const handleSelectAll = useCallback(event => {
    const selectedValues = data.map(item => item[idKey]);
    if(event.target.checked) {
      setCurrentSelectedIds(selectedValues);
    } else {
      setCurrentSelectedIds([]);
    }
    setSelectedIds((state) => {
      if(event.target.checked) {
        return [...new Set([...state, ...selectedValues])]
      }
      return [...new Set([...state.filter((value) => !selectedValues.includes(value))])];
    });
  }, [setSelectedIds, data, idKey, setCurrentSelectedIds]);

  useEffect(() => {
    onDataChange && onDataChange({total, selectedIds, selectedAll})
  }, [total, selectedIds, selectedAll])

  const header = React.useMemo(() => headers.map(h => <TableCell
    className={styles.capitalize}
    key={h}
    onMouseEnter={() => setHeaderHover(h)}
    onMouseLeave={() => setHeaderHover(null)}
    style={canSort(h) ? { cursor: 'pointer' } : {}}
    {...(headerProps && headerProps[h] || {})}
    onClick={handleSort(h)}
  >
    <Grid
      alignItems='center'
      container
      justify={headerAlign && headerAlign[h] || 'flex-start'}
      wrap='nowrap'
    >
      <Grid item>
        {headerMap && headerMap[h] || h}
      </Grid>
      {canSort(h) && <Fade
        in={sortState.key === h && sortState.state !== 'none'}
      >
        <Box
          component='span'
          display='flex'
          pl={1}
        >
          <ArrowDownwardRoundedIcon style={{ transform: `rotate(${sortState.key === h && sortState.state !== 'desc' ? 180 : 0}deg)`, fontSize: '1rem' }} />
        </Box>
      </Fade>}
      {filters && filters[h] && <Fade in={(filterStateMap[h] && !!Object.keys(filterStateMap[h]).length) || headerHover === h}>
        <Box
          mr={1}
          position='absolute'
          right={0}
          style={{transform: 'translateY(-50%)'}}
          top='50%'>
          <FilterComponent
            {...filters[h]}
            setState={handleFilter(h)}
            state={filterStateMap[h]}
          />
        </Box>
      </Fade>}
    </Grid>
  </TableCell>), [headers, canSort, filters, filterStateMap, headerHover, sortState, headerAlign, headerMap, handleSort, headerProps]);

  const body = useMemo(() => data.map(item => (
    <TableRow
      hover
      key={item[idKey]}
      selected={selectedIds.includes(item[idKey])}
      {...(tableRowProps && tableRowProps(item) || {})}
      className={styles.row}
    >
      {selectable && <TableCell padding="checkbox">
        <Checkbox
          checked={selectedAll || selectedIds.includes(item[idKey])}
          color="primary"
          onChange={() =>
            handleSelectOne(item[idKey])
          }
          value={selectedIds.includes(item[idKey])}
        />
      </TableCell>}
      {itemActions && itemActions.length > 0 && <TableCell style={{width: 30 * itemActions.length + (itemActions.length-1)*8+4, padding: '0 0 0 4px'}}>
        <Grid
          alignItems='center'
          className={styles.itemActions}
          container 
        >
          {itemActions.map(action => <Tooltip title={action.tooltip || ''}><IconButton 
            key={action.Icon.key}
            onClick={() => action.onClick(item)} 
            size='small'
          >{action.Icon}</IconButton></Tooltip>)}
        </Grid>
      </TableCell>}
      {headers.map(h => {
        // Have to do this because of typescript limitations
        let cell;
        if (cellMap && cellMap[h]) {
          cell = cellMap[h];
          if (typeof cell === 'function') {
            cell = cell(item);
          }
        } else {
          cell = printCell(item[h]);
        }
        return <TableCell
          key={h}
          {...(cellProps && cellProps[h] || {})}
        >{cell}</TableCell>
      })}
      {additionalColumns.map(({ id, cell, cellProps }) => <TableCell
        key={id}
        {...(cellProps || {})}
      >
        {React.isValidElement(cell) ? cell : cell(item)}
      </TableCell>)}
    </TableRow>
  )), [additionalColumns, headers, cellMap, cellProps, idKey, selectedIds, selectable, data, handleSelectOne, tableRowProps, itemActions])

  return (
    <div className={clsx(styles.root, classes.root)}>
      <Card
        elevation={0}
        style={{overflow: 'unset'}}
      >
        <CardHeader
          action={
            <Grid container>
              <>{(typeof actions === 'function') ? actions(selectedIds, setSelectedIds) : actions}</>
              {setOpen && <Tooltip title={(open ? 'Hide' : 'View')}>
                <IconButton onClick={handleExpand}>
                  {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Tooltip>}
              {loading && <LoadingRing
                className={styles.loading}
                size={24} 
              />}
            </Grid>
          }
          subheader={
            <>
              <Typography
                color="textSecondary"

                variant="body2"
              >
                {total} Records found. Page {page + 1} of{' '}
                {Math.ceil(total / rowsPerPage)}
              </Typography>
              
              {selectableAll &&
                <Typography
                  color="textSecondary"
                  variant="body2"
                >
                  <Checkbox
                    checked={selectedAll}
                    color="primary"
                    onChange={(event, isChecked) => {
                      // setSelectedAll(isChecked)
                      setCurrentSelectedIds([])
                      setSelectedIds([])
                      onSelectedAll && onSelectedAll(isChecked)
                    }}
                  /> Select Entire List
                </Typography>
              }
            </>
          }
          title={title}
        />
        <Divider />
        <Collapse in={open}>
          <CardContent classes={{root: styles.content}}>
            {/* <PerfectScrollbar options={{suppressScrollX: true}}> */}
            <div className={clsx(styles.inner, styles.overflow, classes.wrapper)}>
              <MuiTable {...rest}>
                <TableHead>
                  <TableRow>
                    {selectable && <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedAll || (data.length > 0 && currentSelectedIds.length === data.length)}
                        color="primary"
                        disabled={data.length === 0}
                        indeterminate={
                          data.length > 0 &&
                          currentSelectedIds.length > 0 &&
                          currentSelectedIds.length < data.length
                        }
                        onChange={handleSelectAll}
                      />
                    </TableCell>}
                    {itemActions && itemActions.length > 0 && <TableCell style={{width: 0, padding: 0}}/>}
                    {header}
                    {additionalColumns.map(({ header: h, headerProps: hp, id }) => <TableCell
                      className={styles.capitalize}
                      key={id}
                      {...(hp || {})}
                    >
                      {h}
                    </TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody style={{overflowY: 'auto'}}>
                  {body}
                </TableBody>
              </MuiTable>
            </div>
            {/* </PerfectScrollbar> */}
          </CardContent>
        </Collapse>
        <CardActions className={styles.actions}>
          <TablePagination
            component="div"
            count={total}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            onPageChange={handleChangePage}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </CardActions>
      </Card>
    </div>
  );
};

// React.memo gets rid of generics and thus causes ts compiler errors
// This forces memo type to be coherent
const typedMemo: <T>(c: T) => T = React.memo;

export default typedMemo(BaseTable);
